"use client";

import React from "react";
import Image from "next/image";
import { unstable_ViewTransition as ViewTransition } from 'react'
import MethodCards from "@/components/utils/MethodCards";
import { InView } from "react-intersection-observer";
import NewsList from "@/components/utils/NewsList";
import AccordionLarge from "@/components/utils/AccordionLarge";
import AnchorLink from "@/components/utils/AnchorLink";
import InViewAnimation from "@/components/utils/InViewAnimation";
import Link from "next/link";
import SVG from "react-inlinesvg";

const MethodSection = ({posts}) => {
  
  return (
    <main className="page page-method-detail page-method-agrilca">
      <InView>
      {({ inView, ref, entry }) => (
        <section ref={ref} className={`section-method-kv container-l ${inView?"inViewDark":""}`}>
          <ViewTransition name="method-card-container-2">
            <div className="method-card">
              <div className="method-card-image">
                <img src="/assets/img/method/agrilca/kv-pic.jpg" alt="" />
              </div>
              <div className="method-card-inner">
                <div className="method-card-header">
                  <div className="badge en">
                    <span className="text">Method</span>
                    <span className="number">02</span>
                  </div>
                  <div className="method-card-shoulder bold">土壌の価値の可視化</div>
                  <h1 className="method-card-title en">
                    Agri LCA+
                  </h1>
                  <div className="method-card-description">
                    <div className="anchor-list">
                      <AnchorLink href="#SECTION-1" className="inner-link scaleHover">
                        <span>
                          <i className="icon">
                            <SVG src="/assets/img/common/icon/icon-arrow-anchor.svg" />
                          </i>
                          Agri LCA+とは
                        </span>
                      </AnchorLink>
                      <AnchorLink href="#SECTION-3" className="inner-link scaleHover">
                        <span>
                          <i className="icon">
                            <SVG src="/assets/img/common/icon/icon-arrow-anchor.svg" />
                          </i>
                          国立研究開発法人 産業技術総合研究所（産総研）｜土壌の価値が可視化される意義
                        </span>
                      </AnchorLink>
                    </div>
                    <p className="anotation">北海道の赤井川村コロポックル村。<br />アスパラの伸びをひとつずつ確かめながら、収穫していく。</p>
                  </div>
                </div>
              </div>
            </div>
          </ViewTransition>
        </section>
      )}
      </InView>

      <div className="container-m">
        <section className="section section-gallery">
          <div className="gallery-title bold">
            土の価値を評価し、可視化。<br className="spOnly" />食べ手とつくり手、<br />
            双方にとって本質的な<br className="spOnly" />「いい農業」の指標になります。
          </div>
          <div className="pic-block size-l">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/method/agrilca/gallery/pic-1.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                北海道更別村の松橋農場。広大な畑をトラクターで何度も往復し、畝をまっすぐ刻んでいく。
              </div>
            </InViewAnimation>
          </div>
        </section>

        <div className="separator-asterisk en">*</div>

        <section id="SECTION-1" className="section section-method-agrilca-1">
          <div className="section-title-holder">
            <h2 className="section-title bold">
              Agri LCA+とは
            </h2>
          </div>

          <div className="block n1 sticky-block-container grid-3-col reverse">
            <div className="sticky-item block-title-holder">
              <h2 className="block-title bold">
                農業によって生まれた<br />
                土壌の価値を多角的に可視化
              </h2>
              <div className="block-lead bold">
                「Agri LCA+」は、日本初の「土壌の価値」を可視化する仕組みであり、農業の営み全体を総合的に評価することで、野菜のおいしさや健康にも寄与する土壌、環境負荷の小さい土壌など、目的に応じてさまざまな角度から土壌の価値を可視化することができます。環境影響の評価においては、国立研究開発法人 産業技術総合研究所（産総研）と共に開発した環境影響評価スキームを活用しています。
              </div>
            </div>
            <div className="static-item">
              <picture className="pic sp-wide">
                 <source srcSet="/assets/img/method/agrilca/section-1-pic.png" media="(min-width: 1024px)" />
                  <source srcSet="/assets/img/method/agrilca/section-1-pic-sp.png" media="(max-width: 1024px)" />
                <img src="/assets/img/method/agrilca/section-1-pic.png" alt="" loading="lazy" />
              </picture>
            </div>
          </div>
        </section>
        
        <section className="section section-gallery height-wide">
          <div className="pic-block n1 left">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/method/agrilca/gallery/pic-2.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                ニセコ町近辺の小川にて。土が変わると、水も変わる。畑の先を流れるこの川も、静かにその影響を受けています。
              </div>
            </InViewAnimation>
          </div>
          <div className="pic-block n2 right">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/method/agrilca/gallery/pic-3.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                北海道幕別町の小笠原農場。出荷前に一息。
              </div>
            </InViewAnimation>
          </div>
          <div className="pic-block n3 left">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/method/agrilca/gallery/pic-4.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                北海道幕別町の小笠原農場。放牧されたヤギが草を食む、緑豊かな環境。
              </div>
            </InViewAnimation>
          </div>
        </section>

        <section id="SECTION-3" className="section section-famar-idea">
          <div className="accordion-contianer-wrap">
            <AccordionLarge disabled={true}>
              <div className="idea-title bold">
                食べ手とつくり手の<br className="spOnly" />共通のものさしとして<br />
                土壌の価値が<br className="spOnly" />可視化される意義
              </div>
              <div className="pic-idea-profile">
                <img src="/assets/img/method/agrilca/pic-idea-profile.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-idea-company sansouken">
                <img src="/assets/img/method/agrilca/pic-idea-company.png" alt="" loading="lazy" />
              </div>
              <div className="idea-profile-position bold">
                国立研究開発法人 産業技術総合研究所<br />
                ネイチャーポジティブ技術実装研究センター <br className="spOnly" />自然資本サステナビリティ研究チーム<br />
                兼）安全科学研究部門 <br className="spOnly" />持続可能システム評価研究グループ 研究チーム長
              </div>
              <div className="idea-profile-name bold">
                本下 晶晴さん
              </div>

              <div className="accordion-content">
                <div className="accordion-content-inner">
                  <div className="text">
                    農法と環境との関わりを、イメージだけでなくLCAを用いて定量的に分析し見える形で表示することは、生産者と消費者がともに持続可能な農業への取組みの価値を理解する仕組みとして大変ユニークです。<br />
                    こうした気候変動だけでなく農業に関わるさまざまな環境側面を広く評価することに加えて、土作りや土壌中の微生物環境など、野菜の質に関わるとされる側面も並行して分析することで持続可能な農業の価値を高めていかれることを期待しています。
                  </div>
                </div>
              </div>
            </AccordionLarge>
          </div>
        </section>

        {posts.length > 0 && (
          <>
            <div className="separator-asterisk en">*</div>

            <section className="section section-method-latestcase">
              <div className="section-title-holder">
                <h2 className="section-title bold">
                  最新事例
                </h2>
              </div>
              <div className="container-xs grayCard">
                <NewsList posts={posts} />
                <Link href="/news/implementation/agri_lca/" className="button-link button-round scaleHover size-s">
                  <span className="inner">すべての事例をみる</span>
                </Link>
              </div>
            </section>
          </>
        )}
      </div>

      <section className="section section-method-others">
        <div className="container-l">
          <MethodCards currentNumber={1} className="scaleHover" />
        </div>
      </section>
    </main>
  );
};

export default MethodSection;
